import React, { useEffect, useState } from 'react';
import { api } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, RotateCcw, ShieldOff } from 'lucide-react';
import { toast } from 'sonner';

export function CompetitorSyncCard() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState({});
  const [busyAll, setBusyAll] = useState(false);

  const load = async () => {
    try {
      const r = await api.get('/maps/competitors/status');
      setData(r.data);
    } catch { /* silent */ }
  };

  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);

  const syncOne = async (key, label) => {
    setBusy(b => ({ ...b, [key]: true }));
    try {
      const { data: res } = await api.post(`/maps/competitors/sync?competitor=${key}`);
      const r = res.results?.[key] || {};
      if (r.error) {
        toast.error(`${label}: ${r.error}`);
      } else {
        toast.success(
          `${label}: ${r.total_found} trouvés • ${r.inserted} nouveaux • ${r.updated} MAJ`,
          { duration: 6000 }
        );
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur synchronisation');
    } finally {
      setBusy(b => ({ ...b, [key]: false }));
    }
  };

  const syncAll = async () => {
    setBusyAll(true);
    try {
      const { data: res } = await api.post('/maps/competitors/sync?competitor=all');
      const summaries = Object.entries(res.results || {}).map(([k, r]) =>
        r.error ? `${k}: ❌` : `${k}: +${r.inserted}/${r.total_found}`
      );
      toast.success(`Synchro complète: ${summaries.join(' • ')}`, { duration: 8000 });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur synchronisation globale');
    } finally {
      setBusyAll(false);
    }
  };

  const scrapers = data?.scrapers || [];
  const excelTotal = data?.excel_hanagroup_total || 0;
  const forbiddenWebTotal = scrapers.filter(s => s.is_hanagroup).reduce((s, x) => s + (x.total || 0), 0);
  const oppTotal = data?.competitor_presence_total || 0;

  const fmtDate = (iso) => {
    if (!iso) return 'Jamais';
    try { return new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }); }
    catch { return iso; }
  };

  const hanaScrapers = scrapers.filter(s => s.is_hanagroup);
  const oppScrapers = scrapers.filter(s => !s.is_hanagroup);

  const Row = ({ s, accent }) => (
    <div className="flex items-center gap-3 bg-white/80 rounded-lg px-3 py-2.5 border" style={{ borderColor: accent.border }} data-testid={`competitor-row-${s.key}`}>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{s.label}</div>
        <div className="text-[11px] text-[#71717A] truncate">
          <strong style={{ color: accent.strong }}>{s.localized}</strong>/{s.total} localisés
          <span className="mx-1.5">•</span>
          Dernière synchro : {fmtDate(s.last_synced_at)}
        </div>
      </div>
      <Button size="sm" variant="outline" onClick={() => syncOne(s.key, s.label)}
        disabled={busy[s.key] || busyAll}
        className="h-7 text-xs"
        style={{ borderColor: accent.border, color: accent.strong }}
        data-testid={`competitor-sync-btn-${s.key}`}>
        {busy[s.key] ? <Loader2 className="w-3 h-3 animate-spin mr-1"/> : <RotateCcw className="w-3 h-3 mr-1"/>}
        Synchroniser
      </Button>
    </div>
  );

  return (
    <Card className="border-2 border-[#F97316]/20 shadow-sm bg-gradient-to-br from-[#FFF7ED] to-white" data-testid="competitor-sync-card">
      <CardContent className="p-6 space-y-4">
        <div className="flex items-start gap-2.5">
          <div className="w-9 h-9 rounded-lg bg-[#F97316]/15 flex items-center justify-center flex-shrink-0">
            <ShieldOff className="w-5 h-5 text-[#F97316]" />
          </div>
          <div className="flex-1">
            <h3 className="text-base font-semibold">Synchronisation des corners concurrents</h3>
            <p className="text-xs text-[#71717A] mt-0.5">
              Scrape hebdo automatique (dimanche 03h) + boutons manuels. <strong className="text-[#F97316]">HANAGROUP</strong> seul est bloqué ; les autres concurrents sont des <strong className="text-[#10B981]">opportunités</strong> à démarcher.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2 text-xs">
          <div className="bg-white/60 rounded-lg px-3 py-2 border border-[#F97316]/20">
            <div className="text-[#C2410C] font-semibold text-base">{excelTotal + forbiddenWebTotal}</div>
            <div className="text-[#71717A]">⛔ HANAGROUP interdits</div>
          </div>
          <div className="bg-white/60 rounded-lg px-3 py-2 border border-[#10B981]/30">
            <div className="text-[#047857] font-semibold text-base">{oppTotal}</div>
            <div className="text-[#71717A]">🎯 Opportunités concurrents</div>
          </div>
          <div className="bg-white/60 rounded-lg px-3 py-2 border border-[#71717A]/20">
            <div className="text-[#52525B] font-semibold text-base">{excelTotal}</div>
            <div className="text-[#71717A]">Excel HANAGROUP</div>
          </div>
        </div>

        {/* HANAGROUP — interdiction */}
        {hanaScrapers.length > 0 && (
          <div className="space-y-2 pt-1">
            <div className="flex items-center gap-2 text-xs font-semibold text-[#C2410C] uppercase tracking-wide">
              <span className="w-2 h-2 rounded-full bg-[#F97316]"/>
              HANAGROUP (interdiction absolue)
            </div>
            {hanaScrapers.map((s) => (
              <Row key={s.key} s={s} accent={{ border: '#FED7AA', strong: '#C2410C' }} />
            ))}
          </div>
        )}

        {/* Autres concurrents — opportunités */}
        {oppScrapers.length > 0 && (
          <div className="space-y-2 pt-2">
            <div className="flex items-center gap-2 text-xs font-semibold text-[#047857] uppercase tracking-wide">
              <span className="w-2 h-2 rounded-full bg-[#10B981]"/>
              Concurrents à chasser (opportunités)
            </div>
            {oppScrapers.map((s) => (
              <Row key={s.key} s={s} accent={{ border: '#BBF7D0', strong: '#047857' }} />
            ))}
          </div>
        )}

        <Button variant="outline" onClick={syncAll} disabled={busyAll}
          className="w-full h-9 text-xs border-[#F97316]/40 text-[#9A3412] hover:bg-[#FFEDD5] bg-[#FFF7ED]"
          data-testid="competitor-sync-all-btn">
          {busyAll ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5"/> : <RotateCcw className="w-3.5 h-3.5 mr-1.5"/>}
          Synchroniser tous les concurrents maintenant
        </Button>

        <div className="bg-[#FEF3C7] border border-[#FCD34D] rounded-lg p-2.5 text-[11px] text-[#92400E]">
          💡 <strong>HANAGROUP</strong> → ajoutées à <code>forbidden_positions</code> → ⛔ orange sur carte, blocage Target→Prospect.<br/>
          <strong>Sushi Daily / Sushiman</strong> → ajoutées à <code>competitor_presence</code> → <strong>feu vert pour démarcher</strong>, ces magasins restent appelables. GET <code>/api/maps/competitor-presence</code> pour les afficher sur la carte (à faire côté UI si besoin).
        </div>
      </CardContent>
    </Card>
  );
}
